// T340 · v2 ScoreMeter — the 270° ring the result card's `gauge="ring"` slot
// draws (owner 2026-10-16, log 781: "分数做成是那种meter的ui 动效"). The form is
// the one on the owner's reference site (bfi-canada.com `#estimator`): an arc
// with its gap facing down, the number in the middle, and the number and the arc
// moving TOGETHER when the score changes.
//
// ── what is copied from the reference, and what is not ───────────────────────
// Copied: the ring geometry (viewBox 200, r 88, a 12-unit round-capped stroke,
// the arc starting bottom-left and sweeping 270° clockwise), ONE animated value
// driving both the digits and the arc, and the state starting AT the target —
// the first frame on mount is the final figure, so nothing counts up from zero
// and nothing shifts after load. Only a CHANGE animates.
// Not copied, on purpose:
//   · the reference arc is normalised to 600 while its copy says "of 1,200" —
//     here the arc is value / max, the same scale the printed maximum states
//     (I28: one scale for a displayed figure);
//   · its 650ms tween — I18 caps motion at 600 (MUST); this one is 400;
//   · its red gradient and dark card — one teal (the breakdown bars' colour),
//     no grade bands, no "competitive" wording (I27; the index.css one-accent
//     law); the dark shell was not asked for;
//   · its missing reduced-motion branch — the `[data-v2] *` cut-through in
//     index.css stops CSS transitions, but it cannot reach a requestAnimationFrame
//     loop, so the hook reads the media query itself and jumps to the final
//     figure (I33).
//
// ── the cut-off tick ─────────────────────────────────────────────────────────
// `cutoff` marks the latest VERIFIED round's score with a 2-unit tick across the
// ring. It is a number the caller already holds as a checked value; `null` (or
// anything outside 0…max) draws nothing — the tick is never estimated. The tick
// overhangs the stroke by 4 units on each side, so its two ends always sit on
// the white card (teal-800 on white is the pair the contrast test measures),
// whether or not the fill has passed it.
//
// ── accessibility ────────────────────────────────────────────────────────────
// The root is a `role="meter"` whose aria-value* carry the FINAL figure, never a
// frame. The svg and the counting digits are aria-hidden, so a screen reader
// hears one number, not twenty-four. Its name comes from `labelledBy` (the
// card's eyebrow) plus the visible label under the maximum.
//
// The component computes nothing about a score and carries no copy: the value,
// the maximum, the cut-off and every word arrive from the caller.
import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { cx } from '../cx';

/** The easing budget of the result card — the ring's tween AND the breakdown
 *  bars' width transition. I18: ≤ 600ms (MUST). */
export const V2_SCORECARD_MOTION_MS = 400;

/** I18's hard ceiling. A caller-supplied `motionMs` above it is clamped. */
const I18_CEILING_MS = 600;

// ── ring geometry (viewBox units) ────────────────────────────────────────────
const VIEW = 200;
const C = VIEW / 2;
const R = 88;
const STROKE = 12;
const START_DEG = 135; // bottom-left: the gap is centred at the bottom
const SWEEP_DEG = 270;
const CIRCUMFERENCE = 2 * Math.PI * R;
const round2 = (n: number) => Number(n.toFixed(2));

/** Length of the full 270° arc, in viewBox units. */
export const V2_METER_ARC_LENGTH = round2((CIRCUMFERENCE * SWEEP_DEG) / 360);
/** The dash gap: longer than the rest of the circle, so the arc never repeats. */
const DASH_GAP = Math.ceil(CIRCUMFERENCE);
const TICK_INNER = R - STROKE / 2 - 4;
const TICK_OUTER = R + STROKE / 2 + 4;

/** value / max of the 270° arc, clamped to 0…full, in viewBox units. */
export function arcLength(value: number, max: number): number {
  if (!(max > 0) || !Number.isFinite(value)) return 0;
  const f = Math.min(1, Math.max(0, value / max));
  return round2(V2_METER_ARC_LENGTH * f);
}

/** How far a round cap reaches past the end of its dash: half the stroke. */
export const V2_METER_CAP = STROKE / 2;

/**
 * The fill's dash length, so the TIP of the drawn stroke lands on the value
 * (hub cold review of T340). A round cap reaches V2_METER_CAP past its dash;
 * drawn at the full arcLength, the tip overshot the value by 6 units — about
 * 1.45% of the scale, ≈17 points on 1,200 — and a score just under the cut-off
 * touched or passed the tick while the digits said "under". So: past the cap,
 * the dash is shortened by the cap and keeps its round end; within the first
 * cap's width it is drawn as-is with a butt end (fillLinecap); zero is empty.
 */
export function fillDashLength(value: number, max: number): number {
  const length = arcLength(value, max);
  if (length > V2_METER_CAP) return round2(length - V2_METER_CAP);
  return length;
}

/** The fill's end shape that goes with fillDashLength. */
export function fillLinecap(value: number, max: number): 'round' | 'butt' {
  return arcLength(value, max) > V2_METER_CAP ? 'round' : 'butt';
}

/** Where the cut-off tick sits, in degrees (SVG, clockwise from 3 o'clock) —
 *  or null when there is no verified cut-off inside 0…max to mark. */
export function cutoffAngle(cutoff: number | null | undefined, max: number): number | null {
  if (cutoff === null || cutoff === undefined || !Number.isFinite(cutoff)) return null;
  if (!(max > 0) || cutoff < 0 || cutoff > max) return null;
  return START_DEG + SWEEP_DEG * (cutoff / max);
}

/** The house ease-out: cubic, `1 - (1 - t)^3`. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

/** The figure shown at progress k (0…1) of a tween from → to. The last frame is
 *  `to` itself, never a rounded copy of it (I28: the figure the eye is left with
 *  is the computed total). */
export function scoreFrame(from: number, to: number, k: number): number {
  if (k >= 1) return to;
  if (k <= 0) return from;
  return Math.round(from + (to - from) * easeOutCubic(k));
}

/** SSR-safe read of `prefers-reduced-motion` (the juice.ts shape); false
 *  wherever matchMedia is unavailable. */
export function prefersReducedMotion(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      !!window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

export interface ScoreTweenOptions {
  from: number;
  to: number;
  ms: number;
  /** true ⇒ the final figure at once, and no animation frame is ever requested */
  reduced: boolean;
  onFrame: (value: number) => void;
  /** injectable for tests; default `requestAnimationFrame` / `cancelAnimationFrame` / `performance.now` */
  raf?: (cb: (t: number) => void) => number;
  caf?: (id: number) => void;
  now?: () => number;
}

/**
 * The tween the hook runs, as a plain function so it is testable without a DOM.
 * Returns the cancel function (the effect's cleanup).
 */
export function runScoreTween({
  from,
  to,
  ms,
  reduced,
  onFrame,
  raf = (cb) => requestAnimationFrame(cb),
  caf = (id) => cancelAnimationFrame(id),
  now = () => performance.now(),
}: ScoreTweenOptions): () => void {
  if (from === to) return () => {};
  const duration = Math.min(ms, I18_CEILING_MS);
  if (reduced || !(duration > 0)) {
    onFrame(to);
    return () => {};
  }
  const start = now();
  let live = true;
  let id = 0;
  const tick = (t: number) => {
    if (!live) return;
    const k = Math.min(1, Math.max(0, (t - start) / duration));
    onFrame(scoreFrame(from, to, k));
    if (k < 1) id = raf(tick);
  };
  id = raf(tick);
  return () => {
    live = false;
    caf(id);
  };
}

/**
 * The displayed figure for `target`: equal to it on the first render (no count
 * from zero), then easing to each new target in `ms` — from wherever the
 * previous tween had got to, so a change mid-flight never jumps. Under
 * `prefers-reduced-motion` it is the target, immediately.
 */
export function useAnimatedScore(target: number, ms: number = V2_SCORECARD_MOTION_MS): number {
  const [shown, setShown] = useState(target);
  const shownRef = useRef(target);
  useEffect(
    () =>
      runScoreTween({
        from: shownRef.current,
        to: target,
        ms,
        reduced: prefersReducedMotion(),
        onFrame: (v) => {
          shownRef.current = v;
          setShown(v);
        },
      }),
    [target, ms],
  );
  return shown;
}

export interface V2ScoreMeterProps {
  value: number;
  max: number;
  /** the latest VERIFIED cut-off; null/undefined ⇒ no tick, never an estimate */
  cutoff?: number | null;
  /** the words under "/ max" — e.g. "CRS points"; also part of the meter's name */
  label?: ReactNode;
  /** the tween length; default V2_SCORECARD_MOTION_MS, clamped to 600 (I18) */
  motionMs?: number;
  /** the screen-reader reading of the FINAL figure; default "value / max" */
  valueText?: string;
  /** id of an element that names the meter (the card's eyebrow) */
  labelledBy?: string;
  /** number formatting locale; en and zh both group with commas */
  locale?: string;
  className?: string;
}

export function ScoreMeter({
  value,
  max,
  cutoff = null,
  label,
  motionMs = V2_SCORECARD_MOTION_MS,
  valueText,
  labelledBy,
  locale = 'en-CA',
  className,
}: V2ScoreMeterProps) {
  const autoId = useId();
  const labelId = label ? `v2-meter-${autoId}-label` : undefined;
  const fmt = (n: number) => n.toLocaleString(locale);
  // ONE value drives the digits and the arc — they cannot drift apart
  const shown = useAnimatedScore(value, motionMs);
  // the tip of the drawn stroke lands on the figure the digits show
  const fill = fillDashLength(shown, max);
  const cap = fillLinecap(shown, max);
  const tick = cutoffAngle(cutoff, max);
  const at = (deg: number, r: number) => {
    const a = (deg * Math.PI) / 180;
    return { x: round2(C + r * Math.cos(a)), y: round2(C + r * Math.sin(a)) };
  };
  const tickA = tick === null ? null : at(tick, TICK_INNER);
  const tickB = tick === null ? null : at(tick, TICK_OUTER);
  const names = [labelledBy, labelId].filter(Boolean).join(' ') || undefined;

  return (
    <div
      role="meter"
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuenow={Number.isFinite(value) ? Math.min(max, Math.max(0, value)) : undefined}
      aria-valuetext={valueText ?? `${fmt(value)} / ${fmt(max)}`}
      aria-labelledby={names}
      className={cx('v2-meter relative mx-auto aspect-square w-full max-w-[12.5rem]', className)}
    >
      <svg viewBox={`0 0 ${VIEW} ${VIEW}`} aria-hidden="true" className="block h-full w-full">
        <circle
          className="v2-meter-track stroke-v2-line"
          cx={C}
          cy={C}
          r={R}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={`${V2_METER_ARC_LENGTH} ${DASH_GAP}`}
          transform={`rotate(${START_DEG} ${C} ${C})`}
        />
        <circle
          className="v2-meter-fill stroke-teal-600"
          cx={C}
          cy={C}
          r={R}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap={cap}
          strokeDasharray={`${fill} ${DASH_GAP}`}
          transform={`rotate(${START_DEG} ${C} ${C})`}
        />
        {tickA && tickB ? (
          <line
            className="v2-meter-cutoff stroke-teal-800"
            x1={tickA.x}
            y1={tickA.y}
            x2={tickB.x}
            y2={tickB.y}
            strokeWidth={2}
          />
        ) : null}
      </svg>
      <div
        aria-hidden="true"
        className="absolute inset-0 flex flex-col items-center justify-center px-[16%] text-center"
      >
        <span className="v2-meter-value min-w-[4ch] text-[2.5rem] font-bold leading-none tracking-[-0.02em] tabular-nums text-v2-ink">
          {fmt(shown)}
        </span>
        <span className="mt-2 text-[0.93rem] font-semibold tabular-nums text-v2-muted">/ {fmt(max)}</span>
        {label ? (
          <span id={labelId} className="mt-1 text-[0.82rem] leading-snug text-v2-muted">
            {label}
          </span>
        ) : null}
      </div>
    </div>
  );
}
